import React, { useState } from 'react';
import Plot from 'react-plotly.js';
import { Select, message, Row } from 'antd';


const MAX_TICKERS_AT_ONCE = 10;

const SurfacePlot3D = ({ surfaceVectors, labels }) => {
  const defaultAssets = labels.slice(0, 3);
  const [selectedAssets, setSelectedAssets] = useState(defaultAssets);

  if (!surfaceVectors || surfaceVectors.length === 0) {
    return <div>No data available</div>;
  }

  // Transform volatility data to create more pronounced peaks
  const transformToUniforms = (data) => {
    const maxVal = Math.max(...data.flat());
    return data.map(row =>
      row.map(val => Math.exp(-(1 - val/maxVal) * 5) / maxVal)
    );
  };

  const selectedIndices = selectedAssets.map(asset => labels.indexOf(asset));
  const rawData = surfaceVectors.filter((_, index) => selectedIndices.includes(index));
  const zData = transformToUniforms(rawData);

  const handleSelectChange = (value) => {
    if (value.length < 2) {
      message.warning("Please select at least two assets for comparison.");
    } else if (value.length > MAX_TICKERS_AT_ONCE) {
      message.warning("Maximum 5 assets can be compared simultaneously.");
    } else {
      setSelectedAssets(value);
    }
  };

  return (
    <div className="w-full h-full">
      <Row justify={'center'}>

        <Select
          mode="multiple"
          className="w-[600px] mb-5"
          placeholder="Select assets to analyze volatility surface"
          value={selectedAssets}
          onChange={handleSelectChange}
          maxTagCount={MAX_TICKERS_AT_ONCE}
        >
          {labels.map((label) => (
            <option key={label} value={label}>
              {label}
            </option>
          ))}
        </Select>
      </Row>
      {selectedAssets.length >= 2 && (
        <div className="rounded-xl bg-transparent w-full h-[80vh]">
          <Plot
            useResizeHandler={true}
            data={[
              {
                z: zData,
                x: selectedAssets,
                y: selectedAssets,
                type: 'surface',
                colorscale: [
                  [0, '#E1F5FE'],
                  [0.2, '#81D4FA'],
                  [0.4, '#29B6F6'],
                  [0.6, '#039BE5'],
                  [0.8, '#0288D1'],
                  [1, '#01579B']
                ],
                showscale: true,
                colorbar: {
                  title: 'Volatility Density',
                  titlefont: { color: '#0288D1' },
                  tickfont: { color: '#0288D1' },
                  thickness: 20,
                  len: 0.75
                },
                hoverinfo: 'x+y+z',
                hoverlabel: {
                  bgcolor: '#E1F5FE',
                  bordercolor: '#0288D1',
                  font: { color: '#01579B' }
                },
                hovertemplate:
                  '<b>Asset 1:</b> %{x}<br>' +
                  '<b>Asset 2:</b> %{y}<br>' +
                  '<b>Density:</b> %{z:.4f}<extra></extra>',
                lighting: {
                  ambient: 0.8,
                  diffuse: 1,
                  fresnel: 2,
                  specular: 1.5,
                  roughness: 0.2
                },
                contours: {
                  z: {
                    show: true,
                    usecolormap: true,
                    highlightcolor: "#0288D1",
                    project: { z: true }
                  }
                }
              }
            ]}
            layout={{
              autosize: true,
              paper_bgcolor: 'transparent',
              plot_bgcolor: 'transparent',
              margin: {
                l: 0,
                r: 0,
                b: 0,
                t: 0,
                pad: 0
              },
              scene: {
                camera: {
                  eye: {
                    x: 1.87,
                    y: 0.88,
                    z: 0.64
                  },
                  center: { x: 0, y: 0, z: -0.1 },
                  up: { x: 0, y: 0, z: 1 }
                },
                xaxis: {
                  showgrid: true,
                  gridcolor: '#71bbfd',
                  gridwidth: 1,
                  title: {
                    text: '',
                    font: { color: '#fff', size: 12 }
                  },
                  color: '#fff',
                  tickfont: { color: '#fff', size: 12 }
                },
                yaxis: {
                  showgrid: true,
                  gridcolor: '#0188ff',
                  gridwidth: 1,
                  title: {
                    text: '',
                    font: { color: '#fff', size: 12 }
                  },
                  color: '#0288D1',
                  tickfont: { color: '#fff', size: 16 }
                },
                zaxis: {
                  showgrid: true,
                  gridcolor: '#71bbfd',
                  gridwidth: 2,
                  title: {
                    text: 'Density',
                    font: { color: '#fff', size: 16 }
                  },
                  color: '#fff',
                  tickfont: { color: '#fff', size: 12
                },
                aspectratio: {
                  x: 1.5,
                  y: 1.5,
                  z: 1
                },
                bgcolor: 'transparent'
              }
            }}}

            config={{
              displayModeBar: false,
              responsive: true,
              scrollZoom: true,
              showTips: true
            }}
            style={{
              width: '100%',
              height: '100%'
            }}
          />
        </div>
      )}
    </div>
  );
};

export default SurfacePlot3D;
